import React, { useState } from "react";
import MathContent from "../../../../customComponents/MathContent";
import { useDeepenLearnSessionMutation } from "../../../../../app/learnApiSlice";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  ArrowBack,
  PlayCircleOutline,
  MenuBook,
  KeyboardArrowDown,
  Science,
  EmojiObjects,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const popIn = keyframes`
  from { opacity: 0; transform: scale(0.88) translateY(10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Reader Themes ────────────────────────────────────────────────────────────
const READER_THEMES = {
  white: { bg: "#f8fafc",  surface: "#ffffff",  text: "#1c1c1e", muted: "#6b7280", border: "#e2e8f0",  sectionHeader: "#f3e8ff", headerBorder: "#ddd6fe" },
  sepia: { bg: "#f5efe3",  surface: "#faf6ee",  text: "#3d2b1f", muted: "#7a6250", border: "#d4c4a8",  sectionHeader: "#ede8dc", headerBorder: "#c8b89a" },
  dark:  { bg: "#1c1c1e",  surface: "#2c2c2e",  text: "#e5e5e7", muted: "#98989e", border: "#3a3a3c",  sectionHeader: "#1a1730", headerBorder: "#3a3a3c" },
  black: { bg: "#000000",  surface: "#111111",  text: "#ebebf5", muted: "#8e8e93", border: "#1f1f1f",  sectionHeader: "#0d0d0d", headerBorder: "#1a1a1a" },
};

const FONT_SIZES = [14, 16, 18, 20, 22, 24];

const THEME_SWATCHES = [
  { key: "white", color: "#ffffff", swatch: "#d1d5db" },
  { key: "sepia", color: "#f5ece3", swatch: "#c8b89a" },
  { key: "dark",  color: "#2c2c2e", swatch: "#555555" },
  { key: "black", color: "#000000", swatch: "#333333" },
];

/**
 * Shared lesson ContentViewer.
 *
 * Props:
 *   session          – LearnSession-like object: { subject, level, topics, content }
 *   sessionId        – Numeric ID used to call the /deepen endpoint. Omit to hide the Go Deeper button.
 *   accentColor      – Hex string used for section headers, buttons, chips (e.g. "#7C3AED")
 *   headerGradient   – CSS gradient string for the top header bar
 *   onContinue       – Callback fired when the CTA button is clicked
 *   continueLabel    – CTA button text (default "Take Quiz")
 *   continueIcon     – Optional React element to use as button start icon
 *   isLoadingContinue– Boolean, shows spinner on the CTA button
 *   onGoBack         – Callback for the back arrow in the header
 *   modeLabel        – Small eyebrow label above subject name (default "Learn Mode")
 *   ctaSubtext       – Secondary text under CTA headline
 *   ctaHeadline      – Primary text near CTA button (default "Ready to test yourself?")
 */
const ContentViewer = ({
  session,
  sessionId = null,
  accentColor = "#7C3AED",
  headerGradient = "linear-gradient(135deg, #4A148C 0%, #7C3AED 60%, #A855F7 100%)",
  onContinue,
  continueLabel = "Take Quiz",
  continueIcon = null,
  isLoadingContinue = false,
  onGoBack,
  modeLabel = "Learn Mode",
  ctaHeadline = "Ready to test yourself?",
  ctaSubtext = "Take a short quiz on what you just learned",
}) => {
  // Split intro blocks from deeper blocks (depth > 0 are generated via /deepen)
  const allSessionBlocks = Array.isArray(session?.content) ? session.content : [];
  const introBlocks = allSessionBlocks.filter((b) => !b.depth || b.depth === 0);
  const textBlocks = introBlocks.filter((b) => b.type !== "youtube");
  const youtubeBlocks = introBlocks.filter((b) => b.type === "youtube");

  // Extra blocks added by Go Deeper — live in local state so they appear instantly
  const [extraBlocks, setExtraBlocks] = useState(
    allSessionBlocks.filter((b) => b.depth > 0)
  );
  const currentDepth = extraBlocks.length > 0
    ? Math.max(...extraBlocks.map((b) => b.depth || 0))
    : 0;
  const MAX_DEPTH = 3;

  const [deepenSession, { isLoading: isDeepening }] = useDeepenLearnSessionMutation();

  const handleDeepen = async () => {
    if (!sessionId || isDeepening || currentDepth >= MAX_DEPTH) return;
    try {
      const result = await deepenSession(sessionId).unwrap();
      setExtraBlocks((prev) => [...prev, ...(result.new_blocks || [])]);
    } catch (_) {
      // silently ignore — button remains visible for retry
    }
  };

  const deepenButtonLabel = () => {
    if (currentDepth === 0) return "Go Deeper";
    if (currentDepth === 1) return "Show Examples";
    return "More Examples";
  };
  const deepenButtonIcon = () => {
    if (currentDepth === 0) return <Science sx={{ fontSize: 18 }} />;
    return <EmojiObjects sx={{ fontSize: 18 }} />;
  };

  const [fontSizeIdx, setFontSizeIdx] = useState(1);
  const [themeKey, setThemeKey] = useState("white");
  const [showPanel, setShowPanel] = useState(false);

  const fontSize = FONT_SIZES[fontSizeIdx];
  const T = READER_THEMES[themeKey];
  const isLight = themeKey === "white" || themeKey === "sepia";
  const isDefaultHeader = themeKey === "white";

  // Header text/icon colour adapts to theme
  const headerTextColor = isDefaultHeader ? "#fff" : T.text;
  const headerMutedColor = isDefaultHeader ? "rgba(255,255,255,0.55)" : T.muted;
  const headerBg = isDefaultHeader ? headerGradient : T.sectionHeader;
  const headerBorderStyle = isDefaultHeader ? "none" : `1px solid ${T.headerBorder}`;

  return (
    <Box sx={{ background: T.bg, minHeight: "100vh", pb: 8, transition: "background 0.3s ease" }}>
      {/* ── Header ── */}
      <Box
        sx={{
          background: headerBg,
          borderBottom: headerBorderStyle,
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 3,
          transition: "background 0.3s ease",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          {onGoBack && (
            <IconButton
              onClick={onGoBack}
              size="small"
              sx={{
                color: isDefaultHeader ? "rgba(255,255,255,0.85)" : headerTextColor,
                bgcolor: isDefaultHeader ? "rgba(255,255,255,0.12)" : `${accentColor}22`,
                borderRadius: "10px",
                "&:hover": { bgcolor: isDefaultHeader ? "rgba(255,255,255,0.22)" : `${accentColor}33` },
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 900, fontSize: "0.6rem", color: headerMutedColor, letterSpacing: 3, textTransform: "uppercase" }}>
              {modeLabel}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: isDefaultHeader ? "#fff" : headerTextColor, lineHeight: 1.2 }}>
              {session?.subject || "Your Lesson"}
            </Typography>
          </Box>
          {/* Aa reader controls toggle */}
          <Box
            onClick={() => setShowPanel((p) => !p)}
            sx={{
              px: 1.4, py: 0.55,
              borderRadius: "10px",
              cursor: "pointer",
              bgcolor: showPanel
                ? isDefaultHeader ? "rgba(255,255,255,0.28)" : `${accentColor}44`
                : isDefaultHeader ? "rgba(255,255,255,0.14)" : `${accentColor}22`,
              border: `1px solid ${isDefaultHeader ? "rgba(255,255,255,0.3)" : T.headerBorder}`,
              display: "flex", alignItems: "baseline", gap: 0.3,
              transition: "background 0.15s ease",
              "&:hover": { bgcolor: isDefaultHeader ? "rgba(255,255,255,0.24)" : `${accentColor}33` },
              userSelect: "none",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "0.72rem", color: isDefaultHeader ? "rgba(255,255,255,0.7)" : T.muted, lineHeight: 1 }}>a</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: isDefaultHeader ? "#fff" : headerTextColor, lineHeight: 1 }}>A</Typography>
          </Box>
        </Box>
        {session?.topics?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {session.topics.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                sx={{
                  bgcolor: isDefaultHeader ? "rgba(255,255,255,0.15)" : `${accentColor}22`,
                  color: isDefaultHeader ? "#fff" : headerTextColor,
                  fontWeight: 600, fontSize: "0.72rem",
                  border: `1px solid ${isDefaultHeader ? "rgba(255,255,255,0.25)" : T.headerBorder}`,
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Reader Controls Panel (sticky) ── */}
      {showPanel && (
        <Box
          sx={{
            position: "sticky", top: 0, zIndex: 100,
            bgcolor: T.surface,
            borderBottom: `1.5px solid ${T.border}`,
            px: { xs: 2.5, sm: 3 }, py: 1.8,
            display: "flex", alignItems: "center", gap: { xs: 2, sm: 3 }, flexWrap: "wrap",
            boxShadow: isLight ? "0 3px 16px rgba(0,0,0,0.08)" : "0 3px 20px rgba(0,0,0,0.5)",
            animation: `${popIn} 0.18s ease`,
            transition: "background 0.3s ease",
          }}
        >
          {/* Font size controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: T.muted, letterSpacing: 1.5, textTransform: "uppercase", mr: 0.5 }}>
              Size
            </Typography>
            <IconButton
              size="small"
              disabled={fontSizeIdx === 0}
              onClick={() => setFontSizeIdx((i) => Math.max(0, i - 1))}
              sx={{
                bgcolor: `${accentColor}18`, color: T.text, borderRadius: "8px", width: 32, height: 32,
                "&:hover": { bgcolor: `${accentColor}30` },
                "&.Mui-disabled": { opacity: 0.25, bgcolor: "transparent" },
              }}
            >
              <Typography sx={{ fontSize: "1.3rem", fontWeight: 300, color: "inherit", lineHeight: 1, mt: "-2px" }}>−</Typography>
            </IconButton>
            <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", color: T.text, minWidth: 28, textAlign: "center" }}>{fontSize}</Typography>
            <IconButton
              size="small"
              disabled={fontSizeIdx === FONT_SIZES.length - 1}
              onClick={() => setFontSizeIdx((i) => Math.min(FONT_SIZES.length - 1, i + 1))}
              sx={{
                bgcolor: `${accentColor}18`, color: T.text, borderRadius: "8px", width: 32, height: 32,
                "&:hover": { bgcolor: `${accentColor}30` },
                "&.Mui-disabled": { opacity: 0.25, bgcolor: "transparent" },
              }}
            >
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "inherit", lineHeight: 1 }}>+</Typography>
            </IconButton>
          </Box>

          {/* Divider */}
          <Box sx={{ width: 1, height: 28, bgcolor: T.border, flexShrink: 0 }} />

          {/* Theme swatches */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: T.muted, letterSpacing: 1.5, textTransform: "uppercase", mr: 0.5 }}>
              Theme
            </Typography>
            {THEME_SWATCHES.map(({ key, color, swatch }) => (
              <Box
                key={key}
                onClick={() => setThemeKey(key)}
                sx={{
                  width: 26, height: 26, borderRadius: "50%", bgcolor: color,
                  border: themeKey === key ? `2.5px solid ${accentColor}` : `1.5px solid ${swatch}`,
                  cursor: "pointer",
                  boxShadow: themeKey === key ? `0 0 0 3px ${accentColor}40` : "none",
                  transition: "transform 0.12s ease, box-shadow 0.15s ease",
                  "&:hover": { transform: "scale(1.18)" },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Content blocks ── */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, pt: 3, maxWidth: 1100, mx: "auto" }}>
        {textBlocks.map((block, i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              mb: 2.5, borderRadius: "16px",
              border: `1.5px solid ${T.border}`,
              bgcolor: T.surface, overflow: "hidden",
              animation: `${popIn} 0.4s ${i * 0.06}s ease both`,
              transition: "background 0.3s ease, border-color 0.3s ease",
            }}
          >
            <Box
              sx={{
                px: 2.5, py: 1.5,
                bgcolor: T.sectionHeader,
                borderBottom: `1.5px solid ${T.border}`,
                display: "flex", alignItems: "center", gap: 1,
              }}
            >
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: accentColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MenuBook sx={{ fontSize: 15, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: isLight ? "#1c1c1e" : T.text }}>
                {block.topic || block.heading || `Section ${i + 1}`}
              </Typography>
            </Box>
            <Box
              sx={{
                px: 2.5, py: 2,
                fontSize: `${fontSize}px`, lineHeight: 1.9, color: T.text,
                "& p": { mb: 1.2 },
                "& ul, & ol": { pl: 2.5, mb: 1 },
                "& strong": { fontWeight: 700, color: accentColor },
                "& h2, & h3": { fontWeight: 700, color: accentColor, mt: 1.5, mb: 0.5 },
                transition: "font-size 0.2s ease, color 0.3s ease",
              }}
            >
              <MathContent html={block.content || block.body || ""} />
            </Box>
          </Paper>
        ))}

        {/* ── Go Deeper section ── */}
        {sessionId && (
          <>
            {/* Extra (deep) blocks */}
            {extraBlocks.map((block, i) => {
              const isExample = block.sub_type === "examples" || block.sub_type === "more_examples";
              const blockAccent = isExample ? "#0369A1" : accentColor;
              const blockHeaderBg = isLight
                ? (isExample ? "#e0f2fe" : T.sectionHeader)
                : T.sectionHeader;
              const blockBorder = isLight
                ? (isExample ? "#bae6fd" : T.border)
                : T.border;
              return (
                <Paper
                  key={`deep-${i}`}
                  elevation={0}
                  sx={{
                    mb: 2.5, borderRadius: "16px",
                    border: `1.5px solid ${blockBorder}`,
                    bgcolor: T.surface, overflow: "hidden",
                    animation: `${slideDown} 0.35s ease both`,
                    transition: "background 0.3s ease, border-color 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5, py: 1.5,
                      bgcolor: blockHeaderBg,
                      borderBottom: `1.5px solid ${blockBorder}`,
                      display: "flex", alignItems: "center", gap: 1,
                    }}
                  >
                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: blockAccent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isExample
                        ? <EmojiObjects sx={{ fontSize: 15, color: "#fff" }} />
                        : <Science sx={{ fontSize: 15, color: "#fff" }} />}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: isLight ? "#1c1c1e" : T.text }}>
                      {block.topic || `Section ${textBlocks.length + i + 1}`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 2.5, py: 2,
                      fontSize: `${fontSize}px`, lineHeight: 1.9, color: T.text,
                      "& p": { mb: 1.2 },
                      "& ul, & ol": { pl: 2.5, mb: 1 },
                      "& strong": { fontWeight: 700, color: blockAccent },
                      "& h2, & h3": { fontWeight: 700, color: blockAccent, mt: 1.5, mb: 0.5 },
                      transition: "font-size 0.2s ease, color 0.3s ease",
                    }}
                  >
                    <MathContent html={block.content || block.body || ""} />
                  </Box>
                </Paper>
              );
            })}

            {/* Go Deeper button */}
            {currentDepth < MAX_DEPTH && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleDeepen}
                  disabled={isDeepening}
                  startIcon={isDeepening ? <CircularProgress size={16} /> : deepenButtonIcon()}
                  endIcon={!isDeepening && <KeyboardArrowDown />}
                  sx={{
                    borderRadius: "24px",
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    px: 3,
                    py: 1,
                    borderColor: `${accentColor}55`,
                    color: accentColor,
                    bgcolor: `${accentColor}0d`,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: accentColor,
                      bgcolor: `${accentColor}18`,
                    },
                    "&.Mui-disabled": { opacity: 0.5 },
                  }}
                >
                  {isDeepening ? "Generating…" : deepenButtonLabel()}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* YouTube blocks */}
        {youtubeBlocks.map((block, i) => (
          <Paper
            key={`yt-${i}`}
            elevation={0}
            sx={{
              mb: 2.5, borderRadius: "16px",
              border: `1.5px solid ${isLight ? "#fee2e2" : T.border}`,
              bgcolor: T.surface, overflow: "hidden",
              animation: `${popIn} 0.4s ${(textBlocks.length + i) * 0.06}s ease both`,
              transition: "background 0.3s ease, border-color 0.3s ease",
            }}
          >
            <Box
              sx={{
                px: 2.5, py: 1.5,
                bgcolor: isLight ? "#fff1f2" : T.surface,
                borderBottom: `1.5px solid ${isLight ? "#fee2e2" : T.border}`,
                display: "flex", alignItems: "center", gap: 1,
              }}
            >
              <PlayCircleOutline sx={{ color: isLight ? "#DC2626" : "#f87171", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: isLight ? "#991b1b" : "#f87171" }}>
                {block.title || block.topic || "Related Video"}
              </Typography>
            </Box>
            <Box sx={{ position: "relative", paddingBottom: "56.25%" }}>
              <iframe
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                src={`https://www.youtube.com/embed/${block.video_id}`}
                title={block.title || "Related learning video"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </Paper>
        ))}

        {/* CTA bar */}
        {onContinue && (
          <Box
            sx={{
              p: 2.5,
              background: headerGradient,
              borderRadius: "16px",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mt: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: "1.05rem" }}>{ctaHeadline}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.83rem" }}>{ctaSubtext}</Typography>
            </Box>
            <Button
              variant="contained"
              onClick={onContinue}
              disabled={isLoadingContinue}
              startIcon={isLoadingContinue ? <CircularProgress size={16} sx={{ color: accentColor }} /> : continueIcon}
              sx={{
                bgcolor: "#fff", color: accentColor, fontWeight: 800, borderRadius: "12px",
                px: 2.5, whiteSpace: "nowrap", flexShrink: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
                "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.5)" },
              }}
            >
              {isLoadingContinue ? "Loading…" : continueLabel}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContentViewer;
